import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Car, Plus, Edit, Trash2, FileText, Calendar, Search, ChevronDown, Check, AlertCircle, Loader2, X } from 'lucide-react';
import VehicleImage from '../components/ui/VehicleImage';
import VehicleDetailView from '../components/admin/VehicleDetailView';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Searchable Dropdown Component
const SearchableDropdown = ({ 
  label, 
  value, 
  options = [], 
  onChange, 
  placeholder, 
  disabled = false,
  loading = false,
  required = true,
  allowCustom = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter(opt => 
      (typeof opt === 'string' ? opt : opt.name).toLowerCase().includes(lowerSearch)
    );
  }, [options, searchTerm]);

  // Get display value
  const displayValue = value || '';
  
  const handleSelect = (option) => {
    const val = typeof option === 'string' ? option : option.name;
    onChange(val);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (allowCustom) {
      onChange(e.target.value);
    }
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div className="relative">
      <Label style={{ color: '#E6EDF3' }}>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="relative mt-1">
        <Input
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          onBlur={() => {
            // Delay to allow click on option
            setTimeout(() => setIsOpen(false), 200);
          }}
          placeholder={loading ? 'Laden...' : (disabled ? 'Bitte wählen Sie zuerst...' : placeholder)}
          disabled={disabled || loading}
          className="pr-10"
          style={{ 
            backgroundColor: disabled ? '#1A1F26' : '#0B0F14', 
            borderColor: '#1F2937', 
            color: '#E6EDF3' 
          }}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#94A3B8' }} />
          ) : (
            <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />
          )}
        </div>
        
        {/* Dropdown Menu */}
        {isOpen && !disabled && !loading && (
          <div 
            className="absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-md border shadow-lg"
            style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm" style={{ color: '#94A3B8' }}>
                {searchTerm ? 'Keine Ergebnisse' : 'Keine Optionen verfügbar'}
              </div>
            ) : (
              filteredOptions.map((option, idx) => {
                const optValue = typeof option === 'string' ? option : option.name;
                const isSelected = optValue === value;
                return (
                  <div
                    key={idx}
                    className="px-3 py-2 cursor-pointer flex items-center justify-between transition-colors"
                    style={{ 
                      backgroundColor: isSelected ? '#1EA0E620' : 'transparent',
                      color: '#E6EDF3'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#1EA0E610'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = isSelected ? '#1EA0E620' : 'transparent'}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                  >
                    <span className="text-sm">{optValue}</span>
                    {isSelected && <Check className="w-4 h-4" style={{ color: '#1EA0E6' }} />}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const VehiclesPage = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [sessions, setSessions] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, vehicleId: null, vehicleName: '' });
  
  // Vehicle form state
  const [newVehicle, setNewVehicle] = useState({
    brand: '',
    model: '',
    engine: '',
    year: new Date().getFullYear(),
    vin: '',
    license_plate: ''
  });
  
  // NEU: Vehicle Detail View PopUp State
  const [selectedVehicleForDetails, setSelectedVehicleForDetails] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // NEU: Basis Fahrzeugdaten PopUp State
  const [selectedVehicleForBasicData, setSelectedVehicleForBasicData] = useState(null);
  const [basicVehicleData, setBasicVehicleData] = useState(null);
  const [loadingBasicData, setLoadingBasicData] = useState(false);
  
  // Catalog data state
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [catalogModels, setCatalogModels] = useState([]);
  const [catalogGenerations, setCatalogGenerations] = useState([]);
  const [catalogEngines, setCatalogEngines] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingGenerations, setLoadingGenerations] = useState(false);
  const [loadingEngines, setLoadingEngines] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  
  // Validation state
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // VIN Decoder state
  const [vinMode, setVinMode] = useState(false); // Toggle zwischen VIN und manueller Eingabe
  const [vinInput, setVinInput] = useState('');
  const [vinLoading, setVinLoading] = useState(false);
  const [vinResult, setVinResult] = useState(null);
  const [vinError, setVinError] = useState(null);

  useEffect(() => {
    fetchVehicles();
    fetchAllSessions();
    fetchSubscription();
    fetchBrands();
    fetchCurrentUser();
  }, []);

  // Fetch current user to check admin status
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user');
    }
  };

  // Helper to check if user has access (admin always has access)
  const hasFeatureAccess = (featureName) => {
    if (currentUser?.is_admin) return true;
    return subscription?.features?.[featureName] === true;
  };

  // Fetch catalog brands on mount
  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vehicle-catalog/brands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatalogBrands(response.data.brands || []);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
      // Fallback to common brands if catalog fails
      setCatalogBrands(['Audi', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Opel', 'Ford', 'Porsche', 'Toyota', 'Honda']);
    } finally {
      setLoadingBrands(false);
    }
  };

  // VIN Decoder - Dekodiert VIN und füllt Felder automatisch aus
  const handleVinDecode = async () => {
    if (!vinInput || vinInput.length !== 17) {
      setVinError('VIN muss genau 17 Zeichen haben');
      return;
    }
    
    setVinLoading(true);
    setVinError(null);
    setVinResult(null);
    
    // Brand name normalization map (abbreviations -> full names)
    const brandNormalizer = {
      'VW': 'Volkswagen',
      'BMW': 'BMW',
      'MERCEDES': 'Mercedes-Benz',
      'MERCEDES-BENZ': 'Mercedes-Benz',
      'MB': 'Mercedes-Benz',
      'AUDI': 'Audi',
      'PORSCHE': 'Porsche',
      'OPEL': 'Opel',
      'FORD': 'Ford',
      'SEAT': 'SEAT',
      'SKODA': 'Skoda',
      'ŠKODA': 'Skoda',
      'TOYOTA': 'Toyota',
      'HONDA': 'Honda',
      'NISSAN': 'Nissan',
      'MAZDA': 'Mazda',
      'HYUNDAI': 'Hyundai',
      'KIA': 'Kia',
      'VOLVO': 'Volvo',
      'PEUGEOT': 'Peugeot',
      'RENAULT': 'Renault',
      'FIAT': 'Fiat',
      'CITROEN': 'Citroën',
      'CITROËN': 'Citroën',
      'ALFA ROMEO': 'Alfa Romeo',
      'JAGUAR': 'Jaguar',
      'LAND ROVER': 'Land Rover',
      'MINI': 'MINI',
      'SMART': 'Smart',
      'TESLA': 'Tesla',
      'LEXUS': 'Lexus',
      'INFINITI': 'Infiniti',
      'SUZUKI': 'Suzuki',
      'SUBARU': 'Subaru',
      'MITSUBISHI': 'Mitsubishi',
      'CHEVROLET': 'Chevrolet',
      'CHRYSLER': 'Chrysler',
      'DODGE': 'Dodge',
      'JEEP': 'Jeep'
    };
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/vehicle-master/vin/decode-user`, 
        { vin: vinInput.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        const data = response.data.data;
        setVinResult(data);
        
        // Normalize brand name
        let normalizedBrand = data.brand || data.manufacturer || '';
        const upperBrand = normalizedBrand.toUpperCase();
        if (brandNormalizer[upperBrand]) {
          normalizedBrand = brandNormalizer[upperBrand];
        }
        
        // Auto-fill the form fields
        setNewVehicle(prev => ({
          ...prev,
          brand: normalizedBrand || prev.brand,
          model: data.model || prev.model,
          year: data.model_year || data.production_year || prev.year,
          engine: data.engine_code || prev.engine,
          vin: vinInput.toUpperCase()
        }));
        
        // Load models if brand was set
        if (normalizedBrand) {
          fetchModels(normalizedBrand);
        }
        
        toast.success('VIN erfolgreich dekodiert!');
      }
    } catch (error) {
      console.error('VIN decode error:', error);
      setVinError(error.response?.data?.detail || 'VIN konnte nicht dekodiert werden');
      toast.error('VIN-Dekodierung fehlgeschlagen');
    } finally {
      setVinLoading(false);
    }
  };

  // Fetch models when brand changes
  const fetchModels = async (brand) => {
    if (!brand) {
      setCatalogModels([]);
      setCatalogEngines([]);
      return;
    }
    
    setLoadingModels(true);
    setCatalogModels([]);
    setCatalogEngines([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vehicle-catalog/brands/${encodeURIComponent(brand)}/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatalogModels(response.data.models || []);
    } catch (error) {
      console.error(`Failed to fetch models for ${brand}:`, error);
      setCatalogModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Fetch engines when model changes
  const fetchEngines = async (brand, model) => {
    if (!brand || !model) {
      setCatalogEngines([]);
      return;
    }
    
    setLoadingEngines(true);
    setCatalogEngines([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/vehicle-catalog/brands/${encodeURIComponent(brand)}/models/${encodeURIComponent(model)}/engines`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCatalogEngines(response.data.engines || []);
    } catch (error) {
      console.error(`Failed to fetch engines for ${brand} ${model}:`, error);
      setCatalogEngines([]);
    } finally {
      setLoadingEngines(false);
    }
  };

  // Fetch generations when model changes
  const fetchGenerations = async (brand, model) => {
    if (!brand || !model) {
      setCatalogGenerations([]);
      return;
    }
    
    setLoadingGenerations(true);
    setCatalogGenerations([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/vehicle-catalog/dropdown/generations/${encodeURIComponent(brand)}/${encodeURIComponent(model)}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCatalogGenerations(response.data.generations || []);
    } catch (error) {
      console.error(`Failed to fetch generations for ${brand} ${model}:`, error);
      setCatalogGenerations([]);
    } finally {
      setLoadingGenerations(false);
    }
  };

  // Fetch engines for specific generation
  const fetchEnginesForGeneration = async (brand, model, generation) => {
    if (!brand || !model || !generation) {
      setCatalogEngines([]);
      return;
    }
    
    setLoadingEngines(true);
    setCatalogEngines([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/vehicle-catalog/dropdown/engines/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/${encodeURIComponent(generation)}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Format engines with PS display
      const formattedEngines = (response.data.engines || []).map(eng => ({
        ...eng,
        displayName: `${eng.name}${eng.power_ps ? ` (${eng.power_ps} PS)` : ''}`
      }));
      setCatalogEngines(formattedEngines);
    } catch (error) {
      console.error(`Failed to fetch engines for ${brand} ${model} ${generation}:`, error);
      // Fallback to regular engine fetch
      fetchEngines(brand, model);
    } finally {
      setLoadingEngines(false);
    }
  };

  // Handle brand change - cascade reset model, generation and engine
  const handleBrandChange = (brand) => {
    setNewVehicle(prev => ({ ...prev, brand, model: '', engine: '' }));
    setFormErrors(prev => ({ ...prev, brand: null, model: null, engine: null }));
    setSelectedGeneration(null);
    setCatalogGenerations([]);
    setCatalogEngines([]);
    fetchModels(brand);
  };

  // Handle model change - cascade reset generation and engine, load generations
  const handleModelChange = async (model) => {
    setNewVehicle(prev => ({ ...prev, model, engine: '' }));
    setFormErrors(prev => ({ ...prev, model: null, engine: null }));
    setSelectedGeneration(null);
    setCatalogEngines([]);
    
    // Fetch generations and then auto-match with current year
    if (newVehicle.brand && model) {
      setLoadingGenerations(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/vehicle-catalog/dropdown/generations/${encodeURIComponent(newVehicle.brand)}/${encodeURIComponent(model)}`, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const generations = response.data.generations || [];
        setCatalogGenerations(generations);
        
        // Auto-match generation with current year (prefer newer if overlap)
        const currentYear = newVehicle.year;
        let matchingGens = generations.filter(gen => 
          currentYear >= (gen.year_start || 1900) && currentYear <= (gen.year_end || 2100)
        );
        
        // If no match found (year is newer than all generations), use the newest generation
        if (matchingGens.length === 0 && generations.length > 0) {
          const newestGen = generations.reduce((a, b) => 
            (a.year_end || 0) > (b.year_end || 0) ? a : b
          );
          matchingGens = [newestGen];
        }
        
        if (matchingGens.length > 0) {
          // If multiple matches, prefer the newer generation (higher year_start)
          const bestMatch = matchingGens.reduce((a, b) => 
            (a.year_start || 0) > (b.year_start || 0) ? a : b
          );
          const genCode = bestMatch.code || bestMatch.internal_code;
          setSelectedGeneration(genCode);
          fetchEnginesForGeneration(newVehicle.brand, model, genCode);
        }
      } catch (error) {
        console.error(`Failed to fetch generations for ${newVehicle.brand} ${model}:`, error);
        setCatalogGenerations([]);
      } finally {
        setLoadingGenerations(false);
      }
    }
  };

  // Handle year change - find generation and fetch engines
  const handleYearChange = (val) => {
    const year = parseInt(val);
    setNewVehicle(prev => ({ ...prev, year }));
    setFormErrors(prev => ({ ...prev, year: null }));
    
    // Find matching generation for the selected year (prefer newer if overlap)
    let matchingGens = catalogGenerations.filter(gen => 
      year >= (gen.year_start || 1900) && year <= (gen.year_end || 2100)
    );
    
    // If no match found (year is newer than all generations), use the newest generation
    if (matchingGens.length === 0 && catalogGenerations.length > 0) {
      const newestGen = catalogGenerations.reduce((a, b) => 
        (a.year_end || 0) > (b.year_end || 0) ? a : b
      );
      matchingGens = [newestGen];
    }
    
    if (matchingGens.length > 0) {
      // If multiple matches, prefer the newer generation (higher year_start)
      const bestMatch = matchingGens.reduce((a, b) => 
        (a.year_start || 0) > (b.year_start || 0) ? a : b
      );
      const genCode = bestMatch.code || bestMatch.internal_code;
      setSelectedGeneration(genCode);
      fetchEnginesForGeneration(newVehicle.brand, newVehicle.model, genCode);
    } else {
      setSelectedGeneration(null);
      // Fallback: fetch all engines for the model
      fetchEngines(newVehicle.brand, newVehicle.model);
    }
  };

  // Handle engine change
  const handleEngineChange = (engine) => {
    setNewVehicle(prev => ({ ...prev, engine }));
    setFormErrors(prev => ({ ...prev, engine: null }));
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription');
    }
  };

  // NEU: Fahrzeugdetails für PopUp laden
  const openVehicleDetails = async (vehicle) => {
    setSelectedVehicleForDetails(vehicle);
    setLoadingDetails(true);
    
    try {
      const token = localStorage.getItem('token');
      // Versuche zuerst mit Motorvariante aus vehicle_master_data zu laden
      let response;
      
      // Wenn das Fahrzeug eine Motorvariante hat, suche spezifisch danach
      if (vehicle.engine) {
        try {
          response = await axios.get(
            `${API}/admin/vehicle-master/vehicles/lookup/${encodeURIComponent(vehicle.brand)}/${encodeURIComponent(vehicle.model)}?variant=${encodeURIComponent(vehicle.engine)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (variantError) {
          // Fallback ohne Variante
          response = await axios.get(
            `${API}/admin/vehicle-master/vehicles/lookup/${encodeURIComponent(vehicle.brand)}/${encodeURIComponent(vehicle.model)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } else {
        response = await axios.get(
          `${API}/admin/vehicle-master/vehicles/lookup/${encodeURIComponent(vehicle.brand)}/${encodeURIComponent(vehicle.model)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setVehicleDetails(response.data);
    } catch (error) {
      // Fallback: Basis-Daten aus dem Fahrzeug selbst
      console.error('Could not load vehicle details:', error);
      setVehicleDetails({
        brand: vehicle.brand,
        model: vehicle.model,
        engine: vehicle.engine,
        year: vehicle.year,
        vin: vehicle.vin
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeVehicleDetails = () => {
    setSelectedVehicleForDetails(null);
    setVehicleDetails(null);
  };

  // NEU: Basis Fahrzeugdaten öffnen (nur Grunddaten: Länge, Breite, Höhe, Gewicht, Motorleistung)
  const openBasicVehicleData = async (vehicle) => {
    setSelectedVehicleForBasicData(vehicle);
    setLoadingBasicData(true);
    
    try {
      const token = localStorage.getItem('token');
      // Versuche Fahrzeugdaten aus der Datenbank zu laden
      let response;
      
      try {
        response = await axios.get(
          `${API}/admin/vehicle-master/vehicles/lookup/${encodeURIComponent(vehicle.brand)}/${encodeURIComponent(vehicle.model)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (lookupError) {
        // Fallback: Keine detaillierten Daten verfügbar
        console.log('No detailed data available for basic view');
        response = { data: null };
      }
      
      // Extrahiere nur die Basis-Daten
      const fullData = response.data;
      if (fullData) {
        setBasicVehicleData({
          brand: vehicle.brand,
          model: vehicle.model,
          year: vehicle.year,
          engine: vehicle.engine,
          // Basis-Dimensionen
          length_mm: fullData.dimensions?.length_mm || fullData.specifications?.dimensions?.length_mm,
          width_mm: fullData.dimensions?.width_mm || fullData.specifications?.dimensions?.width_mm,
          height_mm: fullData.dimensions?.height_mm || fullData.specifications?.dimensions?.height_mm,
          weight_kg: fullData.dimensions?.weight_kg || fullData.specifications?.weight?.curb_weight_kg || fullData.weight_kg,
          power_ps: fullData.engine?.power_ps || fullData.specifications?.engine?.power_ps || fullData.power_ps,
          power_kw: fullData.engine?.power_kw || fullData.specifications?.engine?.power_kw,
        });
      } else {
        // Fallback: Nur die Fahrzeuginfo ohne Datenbank-Daten
        setBasicVehicleData({
          brand: vehicle.brand,
          model: vehicle.model,
          year: vehicle.year,
          engine: vehicle.engine,
          length_mm: null,
          width_mm: null,
          height_mm: null,
          weight_kg: null,
          power_ps: null,
        });
      }
    } catch (error) {
      console.error('Could not load basic vehicle data:', error);
      setBasicVehicleData({
        brand: vehicle.brand,
        model: vehicle.model,
        year: vehicle.year,
        engine: vehicle.engine,
      });
    } finally {
      setLoadingBasicData(false);
    }
  };

  const closeBasicVehicleData = () => {
    setSelectedVehicleForBasicData(null);
    setBasicVehicleData(null);
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allSessions = response.data;
      
      // Group sessions by vehicle_id
      const sessionsByVehicle = {};
      allSessions.forEach(session => {
        if (!sessionsByVehicle[session.vehicle_id]) {
          sessionsByVehicle[session.vehicle_id] = [];
        }
        sessionsByVehicle[session.vehicle_id].push(session);
      });
      
      setSessions(sessionsByVehicle);
    } catch (error) {
      console.error('Failed to fetch sessions');
    }
  };

  // Validate form - all fields required EXCEPT vin (Fahrgestellnummer)
  const validateForm = (vehicle) => {
    const errors = {};
    
    if (!vehicle.brand?.trim()) {
      errors.brand = 'Marke ist erforderlich';
    }
    if (!vehicle.model?.trim()) {
      errors.model = 'Modell ist erforderlich';
    }
    if (!vehicle.engine?.trim()) {
      errors.engine = 'Motor ist erforderlich';
    }
    if (!vehicle.year || vehicle.year < 1900 || vehicle.year > new Date().getFullYear() + 1) {
      errors.year = 'Gültiges Baujahr erforderlich';
    }
    // Kennzeichen und VIN sind optional - keine Validierung erforderlich
    
    return errors;
  };

  const handleAddVehicle = async () => {
    // Validate form
    const errors = validateForm(newVehicle);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    // Prevent double submission
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Check if custom input was used (not from catalog)
      const isCustomInput = !catalogBrands.includes(newVehicle.brand) || 
                           !catalogModels.some(m => (typeof m === 'string' ? m : m.name) === newVehicle.model);
      
      let vehicleData = { ...newVehicle };
      
      if (isCustomInput) {
        // Use Auto-Matcher for custom input
        toast.info('KI analysiert Fahrzeugdaten...');
        
        try {
          const matchResponse = await axios.post(`${API}/vehicle-catalog/match-vehicle`, {
            brand: newVehicle.brand,
            model: newVehicle.model,
            engine: newVehicle.engine,
            year: newVehicle.year
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const matchedVehicle = matchResponse.data;
          
          if (matchedVehicle && !matchedVehicle.error) {
            // Update vehicle data with matched info
            vehicleData = {
              ...vehicleData,
              brand: matchedVehicle.manufacturer || vehicleData.brand,
              model: matchedVehicle.model || vehicleData.model,
              engine: matchedVehicle.engine?.name || vehicleData.engine,
              year: matchedVehicle.year || vehicleData.year
            };
            toast.success(`Fahrzeug erkannt: ${matchedVehicle.standardized_name || matchedVehicle.manufacturer + ' ' + matchedVehicle.model}`);
          }
        } catch (matchError) {
          console.warn('Vehicle matching failed, using original data:', matchError);
          // Continue with original data if matching fails
        }
      }
      
      // Create vehicle with the (possibly matched) data
      await axios.post(`${API}/vehicles`, vehicleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Fahrzeug hinzugefügt');
      setShowAddDialog(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Add vehicle error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewVehicle({ 
      brand: '', 
      model: '', 
      engine: '', 
      year: new Date().getFullYear(), 
      vin: '', 
      license_plate: '' 
    });
    setCatalogModels([]);
    setCatalogGenerations([]);
    setCatalogEngines([]);
    setSelectedGeneration(null);
    setFormErrors({});
    // Reset VIN decoder state
    setVinMode(false);
    setVinInput('');
    setVinResult(null);
    setVinError(null);
  };

  // Handle edit vehicle - similar cascade logic for editing
  const [editCatalogModels, setEditCatalogModels] = useState([]);
  const [editCatalogEngines, setEditCatalogEngines] = useState([]);
  const [editLoadingModels, setEditLoadingModels] = useState(false);
  const [editLoadingEngines, setEditLoadingEngines] = useState(false);

  const handleEditBrandChange = async (brand) => {
    setEditingVehicle(prev => ({ ...prev, brand, model: '', engine: '' }));
    setEditCatalogEngines([]);
    
    if (!brand) {
      setEditCatalogModels([]);
      return;
    }
    
    setEditLoadingModels(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vehicle-catalog/brands/${encodeURIComponent(brand)}/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditCatalogModels(response.data.models || []);
    } catch (error) {
      setEditCatalogModels([]);
    } finally {
      setEditLoadingModels(false);
    }
  };

  const handleEditModelChange = async (model) => {
    setEditingVehicle(prev => ({ ...prev, model, engine: '' }));
    
    if (!editingVehicle?.brand || !model) {
      setEditCatalogEngines([]);
      return;
    }
    
    setEditLoadingEngines(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/vehicle-catalog/brands/${encodeURIComponent(editingVehicle.brand)}/models/${encodeURIComponent(model)}/engines`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditCatalogEngines(response.data.engines || []);
    } catch (error) {
      setEditCatalogEngines([]);
    } finally {
      setEditLoadingEngines(false);
    }
  };

  // Load models/engines when editing a vehicle
  useEffect(() => {
    if (editingVehicle) {
      // Load models for the brand
      const loadEditData = async () => {
        if (editingVehicle.brand) {
          setEditLoadingModels(true);
          try {
            const token = localStorage.getItem('token');
            const modelsRes = await axios.get(
              `${API}/vehicle-catalog/brands/${encodeURIComponent(editingVehicle.brand)}/models`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setEditCatalogModels(modelsRes.data.models || []);
            
            // Load engines if model exists
            if (editingVehicle.model) {
              setEditLoadingEngines(true);
              const enginesRes = await axios.get(
                `${API}/vehicle-catalog/brands/${encodeURIComponent(editingVehicle.brand)}/models/${encodeURIComponent(editingVehicle.model)}/engines`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setEditCatalogEngines(enginesRes.data.engines || []);
              setEditLoadingEngines(false);
            }
          } catch (error) {
            console.error('Failed to load edit data');
          } finally {
            setEditLoadingModels(false);
          }
        }
      };
      loadEditData();
    } else {
      setEditCatalogModels([]);
      setEditCatalogEngines([]);
    }
  }, [editingVehicle?.id]);

  const handleUpdateVehicle = async () => {
    // Validate
    const errors = validateForm(editingVehicle);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/vehicles/${editingVehicle.id}`, editingVehicle, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fahrzeug aktualisiert');
      setEditingVehicle(null);
      setFormErrors({});
      fetchVehicles();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteVehicle = async () => {
    const vehicleId = deleteConfirmDialog.vehicleId;
    if (!vehicleId) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fahrzeug gelöscht');
      setDeleteConfirmDialog({ open: false, vehicleId: null, vehicleName: '' });
      fetchVehicles();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      toast.error(`Fehler beim Löschen: ${errorMsg}`);
    }
  };
  
  const openDeleteConfirm = (vehicle) => {
    setDeleteConfirmDialog({ 
      open: true, 
      vehicleId: vehicle.id, 
      vehicleName: `${vehicle.brand} ${vehicle.model}` 
    });
  };

  const getVehicleSessions = (vehicleId) => {
    return sessions[vehicleId] || [];
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return (
      vehicle.brand.toLowerCase().includes(search) ||
      vehicle.model.toLowerCase().includes(search) ||
      vehicle.license_plate?.toLowerCase().includes(search) ||
      vehicle.vin?.toLowerCase().includes(search)
    );
  });

  // Generate year options (last 50 years + next year)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 50; y--) {
      years.push(y.toString());
    }
    return years;
  }, []);

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
              Meine Fahrzeuge
            </h2>
            <p style={{ color: '#94A3B8' }}>Verwalten Sie Ihre Fahrzeuge und sehen Sie deren Diagnosehistorie</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowAddDialog(true);
            }}
            style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Fahrzeug hinzufügen
          </Button>
        </div>

        {/* Search Filter */}
        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#94A3B8' }} />
          <Input
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Suche nach Marke, Modell, Kennzeichen oder VIN..."
            className="pl-10"
            style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
          />
        </div>

        {loading ? (
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        ) : filteredVehicles.length === 0 ? (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <Car className="w-16 h-16 mx-auto mb-4" style={{ color: '#94A3B8' }} />
              <p style={{ color: '#94A3B8' }}>
                {searchFilter ? 'Keine Fahrzeuge gefunden' : 'Noch keine Fahrzeuge vorhanden'}
              </p>
              {!searchFilter && (
                <Button
                  onClick={() => {
                    resetForm();
                    setShowAddDialog(true);
                  }}
                  className="mt-4"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  Erstes Fahrzeug hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filteredVehicles.map((vehicle) => {
              const vehicleSessions = getVehicleSessions(vehicle.id);
              
              return (
                <Card key={vehicle.id} style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-4">
                        <VehicleImage 
                          vehicleId={vehicle.id}
                          brand={vehicle.brand}
                          model={vehicle.model}
                          size="lg"
                        />
                        <div>
                          <CardTitle style={{ color: '#E6EDF3', fontSize: '1.5rem' }}>
                            {vehicle.brand} {vehicle.model}
                          </CardTitle>
                          <div className="mt-2 space-y-1">
                            <p className="text-sm" style={{ color: '#94A3B8' }}>
                              Motor: {vehicle.engine} | Baujahr: {vehicle.year}
                            </p>
                            {vehicle.license_plate && (
                              <p className="text-sm font-semibold" style={{ color: '#7CD4FD' }}>
                                Kennzeichen: {vehicle.license_plate}
                              </p>
                            )}
                            {vehicle.vin && (
                              <p className="text-xs" style={{ color: '#94A3B8' }}>
                                VIN: {vehicle.vin}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {/* Basis Fahrzeugdaten Button - nur wenn KEIN Admin und basic_vehicle_database aber NICHT extended_vehicle_database */}
                        {!currentUser?.is_admin && hasFeatureAccess('basic_vehicle_database') && !hasFeatureAccess('extended_vehicle_database') && (
                          <Button
                            size="sm"
                            onClick={() => openBasicVehicleData(vehicle)}
                            style={{ backgroundColor: '#22C55E', color: '#FFFFFF' }}
                            data-testid="basic-vehicle-data-btn"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Basis Fahrzeugdaten
                          </Button>
                        )}
                        {/* Erweiterte Technischen Fahrzeugdaten Button - Admin hat IMMER Zugriff, oder bei entsprechendem Feature */}
                        {(currentUser?.is_admin || hasFeatureAccess('extended_vehicle_database') || hasFeatureAccess('technical_specifications')) && (
                          <Button
                            size="sm"
                            onClick={() => openVehicleDetails(vehicle)}
                            style={{ backgroundColor: '#1EA0E6', color: '#FFFFFF' }}
                            data-testid="extended-vehicle-data-btn"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Erweiterte Tech-Daten
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => setEditingVehicle(vehicle)}
                          variant="outline"
                          style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openDeleteConfirm(vehicle)}
                          variant="outline"
                          style={{ borderColor: '#EF4444', color: '#EF4444' }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                        <FileText className="w-4 h-4" style={{ color: '#7CD4FD' }} />
                        Diagnosen für dieses Fahrzeug ({vehicleSessions.length})
                      </h4>
                      
                      {vehicleSessions.length === 0 ? (
                        <p className="text-sm" style={{ color: '#94A3B8' }}>
                          Noch keine Diagnosen durchgeführt
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {vehicleSessions.slice(0, 3).map((session) => (
                            <div
                              key={session.id}
                              className="p-3 rounded-lg flex justify-between items-center hover:bg-opacity-80 transition-all"
                              style={{ backgroundColor: '#0B0F14', border: '1px solid #1F2937' }}
                            >
                              <div 
                                className="flex-1 cursor-pointer"
                                onClick={() => navigate(`/report/${session.id}`)}
                              >
                                <p className="text-sm font-medium" style={{ color: '#E6EDF3' }}>
                                  {session.symptom_text.substring(0, 60)}...
                                </p>
                                <p className="text-xs mt-1 flex items-center gap-2" style={{ color: '#94A3B8' }}>
                                  <Calendar className="w-3 h-3" />
                                  {new Date(session.created_at).toLocaleDateString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  })}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {session.status !== 'done' && (
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/diagnosis/session/${session.id}`);
                                    }}
                                    style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                                  >
                                    Fortsetzen
                                  </Button>
                                )}
                                <span
                                  className="px-2 py-1 rounded text-xs whitespace-nowrap"
                                  style={{
                                    backgroundColor: session.status === 'done' ? '#22C55E20' : '#F59E0B20',
                                    color: session.status === 'done' ? '#22C55E' : '#F59E0B'
                                  }}
                                >
                                  {session.status === 'done' ? 'Abgeschlossen' : 'Aktiv'}
                                </span>
                              </div>
                            </div>
                          ))}
                          {vehicleSessions.length > 3 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate('/history')}
                              style={{ borderColor: '#1F2937', color: '#7CD4FD' }}
                            >
                              Alle {vehicleSessions.length} Diagnosen anzeigen
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => navigate(`/diagnosis/start?vehicleId=${vehicle.id}`)}
                      style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                    >
                      Neue Diagnose starten
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add Vehicle Dialog - Updated with Cascading Dropdowns */}
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          if (!open) resetForm();
          setShowAddDialog(open);
        }}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }} className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>Neues Fahrzeug hinzufügen</DialogTitle>
            </DialogHeader>
            
            {/* Required fields notice */}
            <div className="flex items-center gap-2 p-3 rounded-lg mt-2" style={{ backgroundColor: '#1EA0E610', border: '1px solid #1EA0E630' }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#1EA0E6' }} />
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                Felder mit <span className="text-red-500">*</span> sind Pflichtfelder
              </p>
            </div>
            
            {/* VIN/Manual Toggle */}
            <div className="flex gap-2 mt-4">
              <Button
                type="button"
                variant={!vinMode ? "default" : "outline"}
                size="sm"
                onClick={() => setVinMode(false)}
                className={!vinMode ? "bg-cyan-600 hover:bg-cyan-700" : ""}
              >
                Manuell eingeben
              </Button>
              <Button
                type="button"
                variant={vinMode ? "default" : "outline"}
                size="sm"
                onClick={() => setVinMode(true)}
                className={vinMode ? "bg-cyan-600 hover:bg-cyan-700" : ""}
              >
                <Search className="w-4 h-4 mr-1" />
                Per VIN suchen
              </Button>
            </div>
            
            {/* VIN Decoder Section */}
            {vinMode && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}>
                <div className="flex gap-2">
                  <Input
                    placeholder="17-stellige VIN eingeben"
                    value={vinInput}
                    onChange={(e) => setVinInput(e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, ''))}
                    maxLength={17}
                    className="font-mono tracking-wider"
                    style={{ backgroundColor: '#0F141A', borderColor: '#374151', color: '#E6EDF3' }}
                  />
                  <Button
                    onClick={handleVinDecode}
                    disabled={vinLoading || vinInput.length !== 17}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    {vinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dekodieren'}
                  </Button>
                </div>
                
                {vinInput && vinInput.length !== 17 && (
                  <p className="text-xs mt-2 text-amber-400">
                    {vinInput.length}/17 Zeichen
                  </p>
                )}
                
                {vinError && (
                  <p className="text-xs mt-2 text-red-500">{vinError}</p>
                )}
                
                {vinResult && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#0F141A' }}>
                    <p className="text-xs text-green-400 mb-2">✓ VIN erfolgreich dekodiert</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {vinResult.brand && (
                        <div>
                          <span className="text-gray-500">Marke:</span>
                          <span className="text-white ml-1">{vinResult.brand}</span>
                        </div>
                      )}
                      {vinResult.model && (
                        <div>
                          <span className="text-gray-500">Modell:</span>
                          <span className="text-white ml-1">{vinResult.model}</span>
                        </div>
                      )}
                      {(vinResult.model_year || vinResult.production_year) && (
                        <div>
                          <span className="text-gray-500">Baujahr:</span>
                          <span className="text-white ml-1">{vinResult.model_year || vinResult.production_year}</span>
                        </div>
                      )}
                      {vinResult.engine_code && (
                        <div>
                          <span className="text-gray-500">Motor:</span>
                          <span className="text-white ml-1">{vinResult.engine_code}</span>
                        </div>
                      )}
                      {vinResult.body_type && (
                        <div>
                          <span className="text-gray-500">Karosserie:</span>
                          <span className="text-white ml-1">{vinResult.body_type}</span>
                        </div>
                      )}
                      {vinResult.transmission && (
                        <div>
                          <span className="text-gray-500">Getriebe:</span>
                          <span className="text-white ml-1">{vinResult.transmission}</span>
                        </div>
                      )}
                      {vinResult.drive_type && (
                        <div>
                          <span className="text-gray-500">Antrieb:</span>
                          <span className="text-white ml-1">{vinResult.drive_type}</span>
                        </div>
                      )}
                      {vinResult.plant_country && (
                        <div>
                          <span className="text-gray-500">Herkunft:</span>
                          <span className="text-white ml-1">{vinResult.plant_country}</span>
                        </div>
                      )}
                    </div>
                    {vinResult.equipment_codes && vinResult.equipment_codes.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <span className="text-gray-500 text-xs">Ausstattungscodes:</span>
                        <p className="text-xs text-cyan-400 mt-1">
                          {vinResult.equipment_codes.slice(0, 10).join(', ')}
                          {vinResult.equipment_codes.length > 10 && ` +${vinResult.equipment_codes.length - 10} weitere`}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-4 mt-4">
              {/* Brand Dropdown */}
              <div>
                <SearchableDropdown
                  label="Marke"
                  value={newVehicle.brand}
                  options={catalogBrands}
                  onChange={handleBrandChange}
                  placeholder="z.B. BMW, Mercedes, VW"
                  loading={loadingBrands}
                  required={true}
                  allowCustom={true}
                />
                {formErrors.brand && (
                  <p className="text-xs mt-1 text-red-500">{formErrors.brand}</p>
                )}
              </div>
              
              {/* Model Dropdown - depends on Brand */}
              <div>
                <SearchableDropdown
                  label="Modell"
                  value={newVehicle.model}
                  options={catalogModels}
                  onChange={handleModelChange}
                  placeholder="z.B. 320i, E-Klasse, Golf"
                  disabled={!newVehicle.brand}
                  loading={loadingModels}
                  required={true}
                  allowCustom={true}
                />
                {formErrors.model && (
                  <p className="text-xs mt-1 text-red-500">{formErrors.model}</p>
                )}
              </div>
              
              {/* Year Dropdown - BEFORE Engine to filter available engines */}
              <div>
                <SearchableDropdown
                  label="Baujahr"
                  value={newVehicle.year?.toString()}
                  options={yearOptions}
                  onChange={handleYearChange}
                  placeholder="Baujahr wählen"
                  disabled={!newVehicle.model}
                  required={true}
                />
                {selectedGeneration && (
                  <p className="text-xs mt-1" style={{ color: '#7CD4FD' }}>
                    Generation: {selectedGeneration}
                  </p>
                )}
                {formErrors.year && (
                  <p className="text-xs mt-1 text-red-500">{formErrors.year}</p>
                )}
              </div>
              
              {/* Engine Dropdown - depends on Year (filtered by generation) */}
              <div>
                <SearchableDropdown
                  label="Motor"
                  value={newVehicle.engine}
                  options={catalogEngines.map(e => typeof e === 'string' ? e : (e.displayName || e.name || e))}
                  onChange={handleEngineChange}
                  placeholder="z.B. 2.0 TDI (150 PS)"
                  disabled={!newVehicle.year}
                  loading={loadingEngines}
                  required={true}
                  allowCustom={true}
                />
                {formErrors.engine && (
                  <p className="text-xs mt-1 text-red-500">{formErrors.engine}</p>
                )}
              </div>
              
              {/* License Plate - Optional */}
              <div>
                <Label style={{ color: '#E6EDF3' }}>
                  Kennzeichen <span className="text-xs text-gray-500">(optional)</span>
                </Label>
                <Input
                  value={newVehicle.license_plate}
                  onChange={(e) => {
                    setNewVehicle({ ...newVehicle, license_plate: e.target.value });
                  }}
                  placeholder="z.B. M-AB 1234"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              
              {/* VIN - Optional */}
              <div>
                <Label style={{ color: '#E6EDF3' }}>
                  Fahrgestellnummer (VIN) <span className="text-xs text-gray-500">(optional)</span>
                </Label>
                <Input
                  value={newVehicle.vin}
                  onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })}
                  placeholder="17-stellige Fahrzeug-Identifikationsnummer"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              
              <Button
                onClick={handleAddVehicle}
                className="w-full"
                disabled={isSubmitting}
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wird hinzugefügt...
                  </>
                ) : (
                  'Fahrzeug hinzufügen'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Vehicle Dialog - Updated with Cascading Dropdowns */}
        <Dialog open={editingVehicle !== null} onOpenChange={() => {
          setEditingVehicle(null);
          setFormErrors({});
        }}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }} className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>Fahrzeug bearbeiten</DialogTitle>
            </DialogHeader>
            
            {/* Required fields notice */}
            <div className="flex items-center gap-2 p-3 rounded-lg mt-2" style={{ backgroundColor: '#1EA0E610', border: '1px solid #1EA0E630' }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#1EA0E6' }} />
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                Felder mit <span className="text-red-500">*</span> sind Pflichtfelder
              </p>
            </div>
            
            {editingVehicle && (
              <div className="space-y-4 mt-4">
                {/* Brand Dropdown */}
                <div>
                  <SearchableDropdown
                    label="Marke"
                    value={editingVehicle.brand}
                    options={catalogBrands}
                    onChange={handleEditBrandChange}
                    placeholder="z.B. BMW, Mercedes, VW"
                    loading={loadingBrands}
                    required={true}
                    allowCustom={true}
                  />
                  {formErrors.brand && (
                    <p className="text-xs mt-1 text-red-500">{formErrors.brand}</p>
                  )}
                </div>
                
                {/* Model Dropdown */}
                <div>
                  <SearchableDropdown
                    label="Modell"
                    value={editingVehicle.model}
                    options={editCatalogModels}
                    onChange={handleEditModelChange}
                    placeholder="z.B. 320i, E-Klasse, Golf"
                    disabled={!editingVehicle.brand}
                    loading={editLoadingModels}
                    required={true}
                    allowCustom={true}
                  />
                  {formErrors.model && (
                    <p className="text-xs mt-1 text-red-500">{formErrors.model}</p>
                  )}
                </div>
                
                {/* Engine Dropdown */}
                <div>
                  <SearchableDropdown
                    label="Motor"
                    value={editingVehicle.engine}
                    options={editCatalogEngines}
                    onChange={(engine) => setEditingVehicle(prev => ({ ...prev, engine }))}
                    placeholder="z.B. 2.0 TDI, 3.0 V6"
                    disabled={!editingVehicle.model}
                    loading={editLoadingEngines}
                    required={true}
                    allowCustom={true}
                  />
                  {formErrors.engine && (
                    <p className="text-xs mt-1 text-red-500">{formErrors.engine}</p>
                  )}
                </div>
                
                {/* Year Dropdown */}
                <div>
                  <SearchableDropdown
                    label="Baujahr"
                    value={editingVehicle.year?.toString()}
                    options={yearOptions}
                    onChange={(val) => setEditingVehicle(prev => ({ ...prev, year: parseInt(val) }))}
                    placeholder="Baujahr wählen"
                    required={true}
                  />
                  {formErrors.year && (
                    <p className="text-xs mt-1 text-red-500">{formErrors.year}</p>
                  )}
                </div>
                
                {/* License Plate - Optional */}
                <div>
                  <Label style={{ color: '#E6EDF3' }}>
                    Kennzeichen <span className="text-xs text-gray-500">(optional)</span>
                  </Label>
                  <Input
                    value={editingVehicle.license_plate || ''}
                    onChange={(e) => {
                      setEditingVehicle({ ...editingVehicle, license_plate: e.target.value });
                    }}
                    placeholder="z.B. M-AB 1234"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                
                {/* VIN - Optional */}
                <div>
                  <Label style={{ color: '#E6EDF3' }}>
                    Fahrgestellnummer (VIN) <span className="text-xs text-gray-500">(optional)</span>
                  </Label>
                  <Input
                    value={editingVehicle.vin || ''}
                    onChange={(e) => setEditingVehicle({ ...editingVehicle, vin: e.target.value })}
                    placeholder="17-stellige Fahrzeug-Identifikationsnummer"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                
                <Button
                  onClick={handleUpdateVehicle}
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  Speichern
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => setDeleteConfirmDialog({ open, vehicleId: null, vehicleName: '' })}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>Fahrzeug löschen?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p style={{ color: '#94A3B8' }}>
                Möchten Sie das Fahrzeug <strong style={{ color: '#E6EDF3' }}>{deleteConfirmDialog.vehicleName}</strong> wirklich löschen?
              </p>
              <p style={{ color: '#EF4444', fontSize: '0.875rem' }}>
                ⚠️ Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmDialog({ open: false, vehicleId: null, vehicleName: '' })}
                  style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleDeleteVehicle}
                  style={{ backgroundColor: '#EF4444', color: '#ffffff' }}
                >
                  Löschen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* NEU: Vehicle Detail View PopUp */}
        {selectedVehicleForDetails && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-[#0F141A] rounded-xl border border-cyan-500/30 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <Car className="w-6 h-6 text-cyan-400" />
                  <h2 className="text-xl font-bold text-white">
                    {selectedVehicleForDetails.brand} {selectedVehicleForDetails.model}
                  </h2>
                  {selectedVehicleForDetails.year && (
                    <span className="text-gray-400">({selectedVehicleForDetails.year})</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeVehicleDetails}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {loadingDetails ? (
                  <div className="flex items-center justify-center p-12">
                    <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                    <span className="ml-3 text-gray-400">Lade technische Daten...</span>
                  </div>
                ) : vehicleDetails ? (
                  <VehicleDetailView 
                    vehicle={vehicleDetails}
                    onClose={closeVehicleDetails}
                    showHeader={false}
                    isAdmin={currentUser?.is_admin === true}
                    userFeatures={{
                      repair_instructions: subscription?.features?.repair_manuals_access === true,
                      tuning_data: subscription?.features?.tuning_data === true,
                      technical_specifications: subscription?.features?.technical_specifications === true || subscription?.features?.extended_vehicle_database === true,
                    }}
                  />
                ) : (
                  <div className="p-12 text-center text-gray-400">
                    Keine detaillierten Daten verfügbar
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* NEU: Basis Fahrzeugdaten PopUp (nur Grunddaten) */}
        {selectedVehicleForBasicData && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="basic-data-popup">
            <div className="bg-[#0F141A] rounded-xl border border-green-500/30 w-full max-w-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-green-500/10 to-transparent">
                <div className="flex items-center gap-3">
                  <Car className="w-6 h-6 text-green-400" />
                  <h2 className="text-xl font-bold text-white">
                    Basis Fahrzeugdaten
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeBasicVehicleData}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                {loadingBasicData ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-6 h-6 text-green-400 animate-spin" />
                    <span className="ml-3 text-gray-400">Lade Daten...</span>
                  </div>
                ) : basicVehicleData ? (
                  <div className="space-y-4">
                    {/* Fahrzeug-Info */}
                    <div className="text-center pb-4 border-b border-white/10">
                      <h3 className="text-lg font-semibold text-white">
                        {basicVehicleData.brand} {basicVehicleData.model}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {basicVehicleData.engine} • {basicVehicleData.year}
                      </p>
                    </div>
                    
                    {/* Basis-Daten Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Länge */}
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <div className="text-xs text-gray-400 mb-1">Länge</div>
                        <div className="text-lg font-semibold text-white">
                          {basicVehicleData.length_mm 
                            ? `${(basicVehicleData.length_mm / 1000).toFixed(2)} m`
                            : '—'}
                        </div>
                      </div>
                      
                      {/* Breite */}
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <div className="text-xs text-gray-400 mb-1">Breite</div>
                        <div className="text-lg font-semibold text-white">
                          {basicVehicleData.width_mm 
                            ? `${(basicVehicleData.width_mm / 1000).toFixed(2)} m`
                            : '—'}
                        </div>
                      </div>
                      
                      {/* Höhe */}
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <div className="text-xs text-gray-400 mb-1">Höhe</div>
                        <div className="text-lg font-semibold text-white">
                          {basicVehicleData.height_mm 
                            ? `${(basicVehicleData.height_mm / 1000).toFixed(2)} m`
                            : '—'}
                        </div>
                      </div>
                      
                      {/* Gewicht */}
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <div className="text-xs text-gray-400 mb-1">Gewicht</div>
                        <div className="text-lg font-semibold text-white">
                          {basicVehicleData.weight_kg 
                            ? `${basicVehicleData.weight_kg} kg`
                            : '—'}
                        </div>
                      </div>
                      
                      {/* Motorleistung - volle Breite */}
                      <div className="col-span-2 p-4 rounded-lg bg-gradient-to-r from-green-500/10 to-cyan-500/10 border border-green-500/30">
                        <div className="text-xs text-gray-400 mb-1">Motorleistung</div>
                        <div className="text-2xl font-bold text-green-400">
                          {basicVehicleData.power_ps 
                            ? `${basicVehicleData.power_ps} PS`
                            : basicVehicleData.power_kw 
                              ? `${basicVehicleData.power_kw} kW`
                              : '—'}
                          {basicVehicleData.power_ps && basicVehicleData.power_kw && (
                            <span className="text-sm text-gray-400 ml-2">
                              ({basicVehicleData.power_kw} kW)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Hinweis für Upgrade - nur anzeigen wenn NICHT Admin und NICHT extended_vehicle_database */}
                    {!currentUser?.is_admin && !hasFeatureAccess('extended_vehicle_database') && (
                      <div className="mt-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-center">
                        <p className="text-xs text-cyan-400">
                          Für erweiterte technische Daten (Getriebe, Fahrwerk, Ölmengen, etc.) 
                          upgraden Sie auf einen Plan mit &ldquo;Erweiterte Fahrzeugdatenbank&rdquo;.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8">
                    Keine Daten verfügbar
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
